import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ToastContainer, toast } from 'react-toastify';
import { jwtDecode } from 'jwt-decode';
import Router from 'config/routes/Router';
import { checkFullTokenValidation, getAccessToken, clearAccessToken } from 'utils/auth/auth';
import { logout, setSubscriptionCategories } from 'config/redux/slices/userSlice';
import { useTokenRefresh } from 'hooks/useTokenRefresh';
import { profileService } from 'services/auth/authService';
import i18n from 'config/i18n';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import Spinner from 'components/common/Spinner';
import { useTheme } from 'contexts/ThemeContext';

// Define the shape of the JWT payload
interface JwtPayload {
  exp: number;
}

const App: React.FC = () => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();

  // Enable automatic token refresh (refreshes 2 minutes before expiry)
  useTokenRefresh(isLoggedIn);

  useEffect(() => {

    const validateToken = async () => {
      // Skip validation for public routes
      const publicRoutes = ['/login', '/registration', '/', '/verify-otp', '/forgot-password', '/reset-password'];
      if (publicRoutes.includes(location.pathname)) {
        setIsLoggedIn(false);
        setIsLoading(false);
        return;
      }

      // Skip validation if just logged in
      if (localStorage.getItem('justLoggedIn') === 'true') {
        setIsLoggedIn(true);
        localStorage.removeItem('justLoggedIn');

        // Sync language from user's profile preference even when just logged in
        try {
          const profileResponse = await profileService.getProfile();
          const responseData = profileResponse.data as any;
          const preferredLanguage = responseData?.data?.preferred_language || responseData?.preferred_language;
          if (preferredLanguage && i18n.language !== preferredLanguage) {
            await i18n.changeLanguage(preferredLanguage);
          }
          const categories = responseData?.data?.subscription_categories || responseData?.subscription_categories;
          if (Array.isArray(categories)) {
            dispatch(setSubscriptionCategories(categories));
          }
        } catch (profileError) {
          console.warn('Failed to sync language preference:', profileError);
        }

        setIsLoading(false);
        return;
      }

      try {
        const token = getAccessToken();

        if (token) {
          // First check if token is locally valid (not expired)
          // The token refresh mechanism will handle expired tokens
          const isValid = await checkFullTokenValidation(token, dispatch);

          setIsLoggedIn(isValid);
          if (isValid) {
            // Sync language from user's profile preference
            try {
              const profileResponse = await profileService.getProfile();
              const responseData = profileResponse.data as any;
              const preferredLanguage = responseData?.data?.preferred_language || responseData?.preferred_language;
              if (preferredLanguage && i18n.language !== preferredLanguage) {
                await i18n.changeLanguage(preferredLanguage);
              }
              const categories = responseData?.data?.subscription_categories || responseData?.subscription_categories;
              if (Array.isArray(categories)) {
                dispatch(setSubscriptionCategories(categories));
              }
            } catch {
              // Silently fail - language sync is not critical
            }
            // Note: Token refresh is handled by useTokenRefresh hook
          } else {
            navigate('/login');
          }
        } else {
          setIsLoggedIn(false);
          navigate('/login');
        }
      } catch {
        setIsLoggedIn(false);
        navigate('/login');
        toast.error('Failed to validate session. Please log in again.');
      } finally {
        setIsLoading(false);
      }
    };

    validateToken();
  }, [dispatch, navigate, location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      if (window.location.pathname === '/') {
        document.body.style.overflowY = 'scroll';
      } else {
        document.body.style.overflowY = window.innerWidth < 768 ? 'scroll' : 'hidden';
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      document.body.style.overflowY = '';
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  if (isLoading) {
   return <Spinner size="large" text="Loading..." fullPage />;
  }

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}>
      <Router setIsLoggedIn={setIsLoggedIn} />
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={isDark ? 'dark' : 'light'}
        style={{ zIndex: 9999 }}
      />
    </GoogleOAuthProvider>
  );
};

export default App;